/**
 * ragoneCatalogue — supplies the FULL Insights cell catalogue (in the nested
 * `.electrical.*` display shape) to the BATEMO ION cell-selection plot, so it
 * opens looking exactly like the Explorer Ragone (all cells, fixed axes) and
 * the backend ranking just paints state (passed/excluded/ranked) onto it.
 *
 * Source priority:
 *   1. the global `allCells` signal (already loaded when the user came via
 *      /explorer) — reused as-is, identical to the Explorer plot;
 *   2. otherwise lazy-load every /explorer/v2 page and transform with the
 *      shared `transformExplorerItems` (the SAME mapping MainAppPage uses).
 *
 * Pure coordinate + identity helpers (`cellRagoneXY`, `cellPointId`) mirror
 * RagonePlotECharts.getAxisValues / SalesRagonePlot.pointId so a catalogue
 * cell lands at the same spot as its backend overlay point.
 */

import { signal } from '@preact/signals'
import { allCells } from '../../../state/globalSignals.js'
import { getExplorerProducts } from '../../../modules/dataService.js'
import { transformExplorerItems } from '../../../modules/explorerCellShape.js'

/** Module-level cache of the lazily-loaded catalogue (display shape). */
export const catalogueCells = signal([])

/** Load status of the catalogue: 'idle' | 'loading' | 'ready' | 'error'. */
export const catalogueStatus = signal('idle')

let _loading = null

/** Stable identity matching SalesRagonePlot.pointId / the backend rows. */
export function cellPointId(cell) {
  return `${cell && cell.manufacturer}::${cell && (cell.name || cell.model)}`
}

/**
 * Resolve a catalogue cell's {x, y} for the given basis/domain, mirroring
 * RagonePlotECharts.getAxisValues (x = power/current, y = energy/capacity).
 * Returns null when either axis value is missing/non-finite (the cell is then
 * skipped — same as the Explorer plot drops cells without the metric).
 */
export function cellRagoneXY(cell, basis, domain) {
  const e = (cell && cell.electrical) || {}
  let x, y
  if (domain === 'capacity-current') {
    if (basis === 'abs') {
      x = e.I_peak_A
      y = e.C_c10_Ah
    } else if (basis === 'vol') {
      x = e.I_vol_A_per_L
      y = e.C_vol_Ah_per_L
    } else {
      x = e.I_grav_A_per_kg
      y = e.C_grav_Ah_per_kg
    }
  } else {
    // energy-power (default)
    if (basis === 'abs') {
      x = e.P_peak_W
      y = e.E_c10_Wh
    } else if (basis === 'vol') {
      x = e.P_vol_W_per_L
      y = e.E_vol_Wh_per_L
    } else {
      x = e.P_grav_W_per_kg
      y = e.E_grav_Wh_per_kg
    }
  }
  if (!Number.isFinite(x) || !Number.isFinite(y) || x <= 0 || y <= 0) return null
  return { x, y }
}

/** Load every /explorer/v2 page and transform to the display shape. */
async function loadAllExplorer() {
  const first = await getExplorerProducts({ page: 1, limit: 50, sort_by: 'name', sort_order: 'asc' })
  if (!first || !Array.isArray(first.items) || first.items.length === 0) return []
  let items = [...first.items]
  const totalPages = first.pages || 1
  // Remaining pages in batches of 3 (matches MainAppPage's loader).
  for (let page = 2; page <= totalPages; page += 3) {
    const batch = []
    for (let p = page; p < page + 3 && p <= totalPages; p++) batch.push(p)
    const results = await Promise.all(
      batch.map((p) =>
        getExplorerProducts({ page: p, limit: 50, sort_by: 'name', sort_order: 'asc' }),
      ),
    )
    for (const r of results) {
      if (r && Array.isArray(r.items) && r.items.length) items = items.concat(r.items)
    }
  }
  return transformExplorerItems(items)
}

/**
 * Ensure the catalogue is available, populating `catalogueCells`. Prefers the
 * already-loaded `allCells`; otherwise lazy-loads once (deduped). Safe to call
 * repeatedly. Resolves to the catalogue array (possibly empty on failure).
 */
export async function ensureCatalogue() {
  if (catalogueCells.value.length > 0) {
    catalogueStatus.value = 'ready'
    return catalogueCells.value
  }
  if (Array.isArray(allCells.value) && allCells.value.length > 0) {
    catalogueCells.value = allCells.value
    catalogueStatus.value = 'ready'
    return catalogueCells.value
  }
  if (_loading) return _loading
  catalogueStatus.value = 'loading'
  _loading = loadAllExplorer()
    .then((cells) => {
      catalogueCells.value = cells
      catalogueStatus.value = cells.length > 0 ? 'ready' : 'error'
      _loading = null
      return cells
    })
    .catch((err) => {
      console.warn('[ragoneCatalogue] failed to load explorer catalogue:', err)
      catalogueStatus.value = 'error'
      _loading = null
      return []
    })
  return _loading
}

/** Force a fresh load after a failure (clears the empty-cache guard). */
export async function retryCatalogue() {
  catalogueCells.value = []
  catalogueStatus.value = 'idle'
  _loading = null
  return ensureCatalogue()
}
